import {
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import React, { ChangeEvent, Fragment, useState } from "react";
import { Button } from "react-admin";
import SaveActionPassenger from "./SaveActionPassenger";
import PassengerStatusChip from "./PassengerStatusChip";
import {findLast, orderBy} from "lodash";
import moment from "moment/moment";
import ChildFriendlyIcon from "@material-ui/icons/ChildFriendly";
import PassengerUpdatedByField from "./PassengerUpdatedByField";
import { capitalizeWords } from "utils/tools";

interface IPassengerDetailModal {
  assignmentLocation: IAssignmentLocation;
  assignmentPassenger: IAssignmentPassenger;
  assignmentId: number;
}

const useStyles = makeStyles({
  avatar: {
    width: "50px",
    height: "50px",
  },
  avatarWithoutCursor: {
    width: "50px",
    height: "50px",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  titleWrapper: {
    display: "flex",
  },
  titleText: {
    lineHeight: "2.5",
    marginLeft: 10,
  },
  actionInput: {
    width: "100%",
    marginTop: 5,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
  passengerWrapper: {
    padding: 10,
    border: "1px solid rgba(0, 0, 0, 0.12);",
    cursor: "pointer",
  },
  dropOffAuthIcon: {
    margin: "0 auto",
    width: "100%",
    fontSize: "4.5rem",
    marginTop: "20px",
  },
});

const PassengerDetailModal = ({
  assignmentLocation,
  assignmentPassenger,
  assignmentId,
}: IPassengerDetailModal) => {
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<string>();

  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAction(undefined);
  };

  const actions = [
    { value: "reset", label: "Not Started" },
    { value: "notTaking", label: "Not Taking" },
    { value: "board", label: "Board" },
    { value: "alight", label: "Alight" },
    { value: "absent", label: "Absent" },
    { value: "excludeStudent", label: "Exclude Student" },
  ];

  const handleChangeAction = (event: ChangeEvent<HTMLInputElement>) => {
    setAction(event.target.value);
  };

  return (
    <Fragment>
      <Grid item xs="auto" onClick={handleOpenDialog}>
        <Grid container className={classes.passengerWrapper}>
          <Grid item xs={4}>
            <Avatar
              className={classes.avatar}
              src={
                assignmentPassenger.student
                  ? assignmentPassenger.student.photoUrl
                  : "https://icon-library.com/images/no-user-image-icon/no-user-image-icon-27.jpg"
              }
            />

            {assignmentPassenger.student?.dropOffAuth && (
              <ChildFriendlyIcon className={classes.dropOffAuthIcon} />
            )}
          </Grid>

          <Grid item xs={8}>
            <Grid container>
              <Grid item xs={12}>
                <PassengerStatusChip
                  status={assignmentPassenger.status}
                  logs={assignmentPassenger.logs}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {assignmentPassenger.locationCode}
                  {" - "}
                  {assignmentPassenger.student.firstName}{" "}
                  {assignmentPassenger.student.lastName}{" "}
                  {`(ID: ${assignmentPassenger.student.id})`}
                </Typography>

                <Typography variant="body2" gutterBottom>
                  {capitalizeWords(assignmentPassenger.student.gender)}
                </Typography>

                <Typography variant="body2" gutterBottom>
                  {assignmentPassenger.student.level}{" "}
                  {assignmentPassenger.student.levelGroup &&
                    `- ${assignmentPassenger.student.levelGroup}`}
                </Typography>

                <Typography variant="body2" align="center" gutterBottom>
                  {`( Parent Contact : ${
                    assignmentPassenger.student.parent?.mobile ?? "-"
                  } )`}
                </Typography>

                <Typography variant="body2" gutterBottom>
                  <Divider />
                </Typography>

                <Typography variant="caption" display="block" gutterBottom>
                  Boarded At :{" "}
                  {(() => {
                    if (assignmentPassenger.logs.length === 0) {
                      return "-";
                    }

                    const findLog = findLast(assignmentPassenger.logs, [
                      "type",
                      "boarded",
                    ]);

                    if (!findLog) {
                      return "-";
                    }

                    return moment(findLog.loggedAt).format("hh:mm A");
                  })()}
                </Typography>

                <Typography variant="caption" display="block" gutterBottom>
                  Alighted At :{" "}
                  {(() => {
                    if (assignmentPassenger.logs.length === 0) {
                      return "-";
                    }

                    const findLog = findLast(assignmentPassenger.logs, [
                      "type",
                      "alighted",
                    ]);

                    if (!findLog) {
                      return "-";
                    }

                    return moment(findLog.loggedAt).format("hh:mm A");
                  })()}
                </Typography>

                {assignmentPassenger.logs.length > 0 && (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <Divider />
                    </Typography>

                    <Typography variant="caption" display="block" gutterBottom>
                      Last Updated By :{" "}
                      {(() => {
                        if (assignmentPassenger.logs.length === 0) {
                          return "-";
                        }

                        const findLatestLog = findLast(orderBy(
                          assignmentPassenger.logs,
                          ["loggedAt"],
                          ["asc"]
                        ));

                        if (!findLatestLog) {
                          return "-";
                        }

                        return <PassengerUpdatedByField record={findLatestLog} data={findLatestLog.updatedBy} />;
                      })()}
                    </Typography>

                    <Typography variant="caption" display="block" gutterBottom>
                      Last Updated At :{" "}
                      {(() => {
                        if (assignmentPassenger.logs.length === 0) {
                          return "-";
                        }

                        const findLatestLog = findLast(orderBy(
                          assignmentPassenger.logs,
                          ["loggedAt"],
                          ["asc"]
                        ));

                        if (!findLatestLog) {
                          return "-";
                        }

                        return moment(findLatestLog.loggedAt).format("hh:mm A");
                      })()}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
      >
        <DialogTitle id="alert-dialog-title">
          <div className={classes.titleWrapper}>
            <Avatar
              className={classes.avatarWithoutCursor}
              src={
                assignmentPassenger.student
                  ? assignmentPassenger.student.photoUrl
                  : "https://icon-library.com/images/no-user-image-icon/no-user-image-icon-27.jpg"
              }
            />

            <div className={classes.titleText}>
              {assignmentPassenger.student.firstName}{" "}
              {assignmentPassenger.student.lastName}{" "}
              {`(ID: ${assignmentPassenger.student.id})`}
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Passenger Detail</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">
                Level : {assignmentPassenger.student.level}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                Status : {assignmentPassenger.status.toLowerCase()}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">
                Level Group : {assignmentPassenger.student.levelGroup ?? "-"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Status</Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                className={classes.actionInput}
                id="standard-select-action"
                select
                label="Actions *"
                value={action}
                onChange={handleChangeAction}
                variant="outlined"
              >
                {actions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <SaveActionPassenger
            action={action}
            assignmentLocationId={assignmentLocation.id}
            assignmentPassengerId={assignmentPassenger.id}
            assignmentId={assignmentId}
          />
          <Button onClick={handleClose} color="default" label="Back" />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default PassengerDetailModal;
